const API_BASE_URL = process.env.REACT_APP_SUITE_API_BASE_URL || 'https://api.suite.clasol.com/api/v1'
const DEFAULT_APPLICATION_CODE = process.env.REACT_APP_SUITE_APPLICATION_CODE || 'QUALITY_PRODUCTION'

const buildUrl = (path, params) => {
  const url = new URL(`${API_BASE_URL}${path}`)

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, value)
    }
  })

  return url.toString()
}

export const suiteApiConfig = {
  baseUrl: API_BASE_URL,
  applicationCode: DEFAULT_APPLICATION_CODE,
}

export class SuiteApiError extends Error {
  constructor(message, response, payload) {
    super(message)
    this.name = 'SuiteApiError'
    this.status = response?.status
    this.payload = payload
  }
}

let refreshPromise = null
let sessionGeneration = 0

const renewSession = () => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const response = await fetch(buildUrl('/auth/refresh'), {
        method: 'POST', credentials: 'include',
        headers: { Accept: 'application/json', 'x-application-code': 'SUITE_HOME' },
      })
      if (!response.ok) {
        const error = new SuiteApiError('La sesion ha caducado. Inicia sesion de nuevo para continuar.', response)
        if ([401, 403].includes(response.status)) window.dispatchEvent(new Event('suite:session-expired'))
        throw error
      }
      sessionGeneration += 1
    })().finally(() => { refreshPromise = null })
  }
  return refreshPromise
}

// Only retry a request rejected by the authentication guard, once. Concurrent
// requests share the refresh and late 401 responses reuse the renewed session.
const fetchWithSession = async (url, options, refreshAllowed = true) => {
  const generation = sessionGeneration
  let response = await fetch(url, options)
  if (response.status === 401 && refreshAllowed) {
    if (generation === sessionGeneration) await renewSession()
    response = await fetch(url, options)
    if (response.status === 401) window.dispatchEvent(new Event('suite:session-expired'))
  }
  return response
}

export const suiteApiRequest = async (path, options = {}) => {
  const {
    applicationCode = DEFAULT_APPLICATION_CODE,
    body,
    headers,
    method = 'GET',
    params,
  } = options

  const requestHeaders = {
    Accept: 'application/json',
    'x-application-code': applicationCode,
    ...headers,
  }

  const requestOptions = {
    method,
    credentials: 'include',
    headers: requestHeaders,
  }

  if (body instanceof FormData) {
    requestOptions.body = body
  } else if (body !== undefined) {
    requestHeaders['Content-Type'] = 'application/json'
    requestOptions.body = JSON.stringify(body)
  }

  const response = await fetchWithSession(buildUrl(path, params), requestOptions, !['/auth/login', '/auth/logout', '/auth/refresh'].includes(path))
  const contentType = response.headers.get('content-type') || ''
  const payload = contentType.includes('application/json')
    ? await response.json().catch(() => null)
    : await response.text().catch(() => '')

  if (!response.ok) {
    throw new SuiteApiError(payload?.message || 'Error al comunicarse con Suite API', response, payload)
  }

  return payload
}

export const login = (credentials) => (
  suiteApiRequest('/auth/login', {
    method: 'POST',
    body: credentials,
    applicationCode: 'SUITE_HOME',
  })
)

export const logout = () => (
  suiteApiRequest('/auth/logout', {
    method: 'POST',
    applicationCode: 'SUITE_HOME',
  })
)

export const getCurrentSession = () => (
  suiteApiRequest('/auth/me', {
    applicationCode: 'SUITE_HOME',
  })
)

export const getQualityStatuses = () => (
  suiteApiRequest('/quality-production/statuses')
)

export const getQualityOrders = (params = {}) => (
  suiteApiRequest('/quality-production/orders', { params })
)

export const prepareQualityOrderLine = (payload) => (
  suiteApiRequest('/quality-production/orders/source-line', { method: 'POST', body: payload })
)

export const getQualitySourceLines = (params = {}) => (
  suiteApiRequest('/quality-production/source-lines', {
    params: {
      page: 1,
      limit: 100,
      sortDirection: 'asc',
      ...params,
    },
  })
)

export const createQualitySourceLine = (payload) => (
  suiteApiRequest('/quality-production/source-lines', {
    method: 'POST',
    body: payload,
  })
)

export const updateQualitySourceLine = (id, payload) => (
  suiteApiRequest(`/quality-production/source-lines/${id}`, {
    method: 'PATCH',
    body: payload,
  })
)

export const getQualityControls = (params = {}) => (
  suiteApiRequest('/quality-production/controls', {
    params: {
      page: 1,
      limit: 100,
      sortBy: 'createdAt',
      sortDirection: 'desc',
      ...params,
    },
  })
)

export const getQualityControl = (id) => (
  suiteApiRequest(`/quality-production/controls/${id}`)
)

export const createQualityControl = (payload) => (
  suiteApiRequest('/quality-production/controls', {
    method: 'POST',
    body: payload,
  })
)

export const updateQualityControl = (id, payload) => (
  suiteApiRequest(`/quality-production/controls/${id}`, {
    method: 'PATCH',
    body: payload,
  })
)

export const updateQualityControlStatus = (id, payload) => (
  suiteApiRequest(`/quality-production/controls/${id}/status`, {
    method: 'PATCH',
    body: payload,
  })
)

export const uploadFile = (file) => {
  const formData = new FormData()
  formData.append('file', file)

  return suiteApiRequest('/files', {
    method: 'POST',
    body: formData,
  })
}

export const getFileDataUrl = async (uuid) => {
  const response = await fetchWithSession(buildUrl(`/files/${encodeURIComponent(uuid)}/download`), {
    credentials: 'include', headers: { 'x-application-code': DEFAULT_APPLICATION_CODE },
  })
  if (!response.ok) throw new SuiteApiError('No se ha podido consultar la imagen del control', response)
  const blob = await response.blob()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(new Error('No se ha podido leer la imagen del control'))
    reader.readAsDataURL(blob)
  })
}

export const linkFile = (uuid, data) => (
  suiteApiRequest(`/files/${uuid}/link`, {
    method: 'POST',
    body: data,
  })
)
